<template>
  <div class="flex flex-col h-full">
    <!-- Header -->
    <div class="flex-shrink-0 pb-4">
      <h3 class="text-lg font-semibold text-white">Manage Members</h3>
      <p class="text-sm text-gray-400 mt-1">{{ listTitle }}</p>
    </div>

    <!-- Tabs -->
    <div class="flex-shrink-0 flex border-b border-gray-700/50 mb-4">
      <button
        :class="[
          'flex-1 py-3 text-sm font-medium transition-colors relative',
          activeTab === 'members'
            ? 'text-white'
            : 'text-gray-500 hover:text-gray-300'
        ]"
        @click="activeTab = 'members'">
        <span class="flex items-center justify-center gap-2">
          <fa-icon :icon="['fas', 'users']" class="text-xs" />
          Members
          <span
            v-if="memberCount > 0"
            class="px-1.5 py-0.5 text-xs bg-gray-700 rounded-full">
            {{ memberCount }}
          </span>
        </span>
        <div
          v-if="activeTab === 'members'"
          class="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-500" />
      </button>
      <button
        :class="[
          'flex-1 py-3 text-sm font-medium transition-colors relative',
          activeTab === 'invites'
            ? 'text-white'
            : 'text-gray-500 hover:text-gray-300'
        ]"
        @click="activeTab = 'invites'">
        <span class="flex items-center justify-center gap-2">
          <fa-icon :icon="['fas', 'envelope']" class="text-xs" />
          Invites
          <span
            v-if="inviteCount > 0"
            class="px-1.5 py-0.5 text-xs bg-amber-500/20 text-amber-400 rounded-full">
            {{ inviteCount }}
          </span>
        </span>
        <div
          v-if="activeTab === 'invites'"
          class="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-500" />
      </button>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto min-h-0">
      <!-- Members Tab -->
      <div v-if="activeTab === 'members'" class="space-y-2">
        <!-- Loading state -->
        <div
          v-if="loadingMembers"
          class="py-8 text-center text-gray-500 text-sm">
          <fa-icon :icon="['fas', 'spinner']" class="animate-spin mr-2" />
          Loading members...
        </div>

        <!-- Members list -->
        <template v-else-if="members.length > 0">
          <div
            v-for="member in members"
            :key="member.id"
            class="flex items-center gap-3 p-3 rounded-lg bg-gray-800/50">
            <!-- Avatar -->
            <UserAvatar :user="member.profiles" size="10" />

            <!-- Info -->
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-white truncate">
                {{ member.profiles?.displayname || member.profiles?.username }}
              </p>
              <p class="text-xs text-gray-500">
                <span
                  v-if="member.role === 'owner'"
                  class="text-indigo-400 font-medium">
                  Owner
                </span>
                <template v-else>
                  @{{ member.profiles?.username }} · Joined
                  {{ formatTimeAgo(member.joined_at) }}
                </template>
              </p>
            </div>

            <!-- Remove button (owner only, can't remove self/owner) -->
            <button
              v-if="member.role !== 'owner'"
              :disabled="removingMember === member.user_id"
              class="p-2 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
              title="Remove member"
              @click="handleRemoveMember(member)">
              <fa-icon
                :icon="
                  removingMember === member.user_id
                    ? ['fas', 'spinner']
                    : ['fas', 'times']
                "
                :class="{
                  'animate-spin': removingMember === member.user_id
                }" />
            </button>
          </div>
        </template>

        <!-- Empty state -->
        <div v-else class="py-12 text-center">
          <fa-icon
            :icon="['fas', 'users']"
            class="text-3xl text-gray-600 mb-3" />
          <p class="text-sm text-gray-500">No members yet</p>
          <p class="text-xs text-gray-600 mt-1">
            When people join, they'll appear here
          </p>
        </div>
      </div>

      <!-- Invites Tab -->
      <div v-else-if="activeTab === 'invites'" class="space-y-2">
        <!-- Loading state -->
        <div
          v-if="loadingInvites"
          class="py-8 text-center text-gray-500 text-sm">
          <fa-icon :icon="['fas', 'spinner']" class="animate-spin mr-2" />
          Loading invites...
        </div>

        <!-- Invites list -->
        <template v-else-if="invites.length > 0">
          <div
            v-for="invite in invites"
            :key="invite.id"
            class="p-3 rounded-lg bg-gray-800/50 border border-gray-700/50">
            <div class="flex items-start gap-3">
              <!-- Avatar -->
              <UserAvatar :user="invite.invited_user" size="10" />

              <!-- Info -->
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-white">
                  {{
                    invite.invited_user?.displayname ||
                    invite.invited_user?.username
                  }}
                </p>
                <p class="text-xs text-gray-500">
                  @{{ invite.invited_user?.username }}
                </p>
                <p class="mt-1 text-xs text-gray-600">
                  Invited {{ formatTimeAgo(invite.created_at) }}
                </p>
              </div>

              <!-- Status badge -->
              <span
                :class="[
                  'px-2 py-0.5 text-xs font-medium rounded-full',
                  invite.status === 'pending'
                    ? 'bg-amber-500/20 text-amber-400'
                    : invite.status === 'accepted'
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-gray-500/20 text-gray-400'
                ]">
                {{ invite.status }}
              </span>
            </div>

            <!-- Actions -->
            <div v-if="invite.status === 'pending'" class="flex gap-2 mt-3">
              <button
                :disabled="processingInvite === invite.id"
                class="flex-1 py-2 px-3 text-sm font-medium text-gray-300 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                @click="handleCancelInvite(invite)">
                <fa-icon
                  v-if="processingInvite === invite.id"
                  :icon="['fas', 'spinner']"
                  class="animate-spin mr-1" />
                <fa-icon v-else :icon="['fas', 'times']" class="mr-1" />
                Cancel Invite
              </button>
            </div>
          </div>
        </template>

        <!-- Empty state -->
        <div v-else class="py-12 text-center">
          <fa-icon
            :icon="['fas', 'envelope']"
            class="text-3xl text-gray-600 mb-3" />
          <p class="text-sm text-gray-500">No pending invites</p>
          <p class="text-xs text-gray-600 mt-1">
            Invite users to join your list
          </p>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="flex-shrink-0 pt-4 mt-auto">
      <button
        type="button"
        class="w-full py-3 text-sm font-medium text-gray-400 rounded-lg cursor-pointer hover:text-white hover:bg-white/10 transition-all"
        @click="close">
        Done
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useStoreLists } from '@/stores/storeLists'
import { formatTimeAgo } from '@/use/useFormatDate.js'
import UserAvatar from '@/components/UserAvatar.vue'

const props = defineProps({
  listId: {
    type: String,
    required: true
  },
  listTitle: {
    type: String,
    default: 'List'
  },
  initialTab: {
    type: String,
    default: 'members' // 'members' or 'invites'
  }
})

const emit = defineEmits(['close'])

const storeLists = useStoreLists()

// State
const activeTab = ref(props.initialTab)
const loadingMembers = ref(false)
const loadingInvites = ref(false)
const removingMember = ref(null)
const processingInvite = ref(null)

// Computed
const members = computed(() => {
  const collaborativeMembers = storeLists.getListMembers(props.listId)
  const list = storeLists.getListByIdOrSlug(props.listId)

  // If we don't have list data or owner info, just return collaborative members
  if (!list || !list.user_id || !list.profiles) {
    return collaborativeMembers
  }

  // Check if owner is already in the collaborative members list
  const ownerAlreadyInMembers = collaborativeMembers.some(
    (member) => member.user_id === list.user_id
  )

  // If owner is already in the list, just return collaborative members
  if (ownerAlreadyInMembers) {
    return collaborativeMembers
  }

  // Create owner member object and add to the beginning of the list
  const ownerMember = {
    id: `owner_${list.user_id}`,
    user_id: list.user_id,
    role: 'owner',
    status: 'active',
    joined_at: list.created_at,
    profiles: list.profiles
  }

  return [ownerMember, ...collaborativeMembers]
})
const invites = computed(() => storeLists.listUserInvites[props.listId] || [])
const memberCount = computed(() => members.value.length)
const inviteCount = computed(
  () => invites.value.filter((i) => i.status === 'pending').length
)

// Methods
const close = () => {
  emit('close')
}

const fetchMembers = async () => {
  loadingMembers.value = true
  try {
    await storeLists.fetchListMembers(props.listId)
  } catch (error) {
    console.error('Error fetching members:', error)
  } finally {
    loadingMembers.value = false
  }
}

const fetchInvites = async () => {
  loadingInvites.value = true
  try {
    await storeLists.fetchUserInvites(props.listId)
  } catch (error) {
    console.error('Error fetching invites:', error)
  } finally {
    loadingInvites.value = false
  }
}

const handleRemoveMember = async (member) => {
  if (removingMember.value) return

  const name =
    member.profiles?.displayname || member.profiles?.username || 'this member'
  if (!confirm(`Remove ${name} from this list?`)) return

  removingMember.value = member.user_id
  try {
    await storeLists.removeMember(props.listId, member.user_id)
  } catch (error) {
    console.error('Error removing member:', error)
  } finally {
    removingMember.value = null
  }
}

const handleCancelInvite = async (invite) => {
  if (processingInvite.value) return

  processingInvite.value = invite.id
  try {
    await storeLists.cancelUserInvite(props.listId, invite.id)
  } catch (error) {
    console.error('Error cancelling invite:', error)
  } finally {
    processingInvite.value = null
  }
}

// Fetch data on mount
onMounted(() => {
  fetchMembers()
  fetchInvites()
})

// Watch for tab changes to ensure data is loaded
watch(activeTab, (newTab) => {
  if (
    newTab === 'members' &&
    members.value.length === 0 &&
    !loadingMembers.value
  ) {
    fetchMembers()
  } else if (
    newTab === 'invites' &&
    invites.value.length === 0 &&
    !loadingInvites.value
  ) {
    fetchInvites()
  }
})
</script>
